const en = {
  panel: {
    open: 'Open accessibility settings',
    close: 'Close accessibility settings',
    title: 'Accessibility',
    subtitle: 'Customize display, colors, typography and reading assistance.',
    reset: 'Reset all settings',
  },
  sections: {
    vision: 'Vision & reading',
    colorVision: 'Color vision',
    themes: 'Display themes',
    typography: 'Typography',
    reader: 'Vocal reader',
    motion: 'Motion',
  },
  vision: {
    hint: 'Increase text size and spacing for low vision.',
    textSize: 'Text size',
    spacing: 'Increased spacing',
    spacingDesc: 'More space between letters, words and lines.',
    readingMode: 'Reading mode',
    readingModeDesc: 'Centred column, reduced visual noise.',
    enhancedFocus: 'Enhanced focus',
    enhancedFocusDesc: 'More visible keyboard focus outlines.',
  },
  colorVision: {
    hint: 'Adaptive themes for your color vision profile (UI, images, videos and embedded players).',
    none: 'Standard vision',
    noneDesc: 'Normal color vision. Colors are displayed without a special filter.',
    deuteranopia: 'Deuteranopia',
    deuteranopiaDesc:
      'Red-green (green cones). Greens and reds can be difficult to distinguish and may appear closer to yellow, brown, gray or blue.',
    protanopia: 'Protanopia',
    protanopiaDesc:
      'Red-green (red cones). Reds become darker and may be confused with green, brown or gray.',
    tritanopia: 'Tritanopia',
    tritanopiaDesc:
      'Blue-yellow. Blue and green, as well as yellow and pink-violet, can be harder to distinguish.',
    achromatopsia: 'Achromatopsia',
    achromatopsiaDesc:
      'Monochrome / grayscale. The interface is presented mainly in shades of gray.',
  },
  themes: {
    default: 'Default (dark)',
    light: 'Light theme',
    lightDesc: 'Light background, dark text.',
    'high-contrast': 'High contrast',
    'high-contrastDesc': 'Maximum contrast for readability.',
    soft: 'Soft / low glare',
    softDesc: 'Reduced brightness and softer accents.',
  },
  typography: {
    default: 'Standard font',
    dyslexia: 'Dyslexia-friendly',
    dyslexiaDesc: 'Lexend font, increased spacing (recommended for dyslexia).',
  },
  reader: {
    hint: 'Text-to-speech vocal reader built into your browser. Select text on the page, then use "Read selection", or read the main content in one go.',
    voice: 'Voice',
    voiceDefault: 'System default',
    voiceFemale: 'Female voice',
    voiceMale: 'Male voice',
    speed: 'Reading speed',
    pitch: 'Voice pitch',
    readSelection: 'Read selection',
    readPage: 'Read main content',
    play: 'Start reading',
    pause: 'Pause',
    resume: 'Resume',
    stop: 'Stop',
    playing: 'Reading aloud...',
    paused: 'Paused',
    started: 'Vocal reading started.',
    noSelection: 'No text selected. Highlight text on the page first.',
    noContent: 'No readable content found on this page.',
    unsupported: 'Text-to-speech is not supported in this browser.',
    regionLabel: 'Vocal reader controls',
    progress: 'Segment {{current}} of {{total}}',
  },
  motion: {
    reduce: 'Reduce animations',
    reduceDesc: 'Minimize motion and transitions.',
  },
  announce: {
    textSize: 'Text size set to {{value}}%.',
    spacing: 'Spacing updated.',
    readingMode: 'Reading mode updated.',
    focus: 'Focus visibility updated.',
    motion: 'Motion preference updated.',
    reset: 'Accessibility settings reset.',
  },
} as const;

const fr = {
  ...en,
  colorVision: {
    hint: 'Adapte les couleurs de l’interface, des images, des vidéos et des lecteurs intégrés à votre profil visuel.',
    none: 'Vision standard',
    noneDesc:
      'Vision normale des couleurs. Les couleurs restent affichées sans filtre particulier.',
    deuteranopia: 'Deutéranopie',
    deuteranopiaDesc:
      'Rouge-vert (cônes verts). Les verts et les rouges peuvent être difficiles à distinguer et se rapprocher de tons jaunes, bruns, gris ou bleutés.',
    protanopia: 'Protanopie',
    protanopiaDesc:
      'Rouge-vert (cônes rouges). Les rouges deviennent plus sombres et peuvent être confondus avec du vert, du brun ou du gris.',
    tritanopia: 'Tritanopie',
    tritanopiaDesc:
      'Bleu-jaune. La distinction entre bleu et vert, ainsi qu’entre jaune et rose-violet, peut être plus difficile.',
    achromatopsia: 'Achromatopsie',
    achromatopsiaDesc:
      'Monochrome / niveaux de gris. L’interface est essentiellement perçue en niveaux de gris.',
  },
} as const;

const es = {
  panel: {
    open: 'Abrir ajustes de accesibilidad',
    close: 'Cerrar ajustes de accesibilidad',
    title: 'Accesibilidad',
    subtitle: 'Personaliza visualización, colores, tipografía y lectura por voz.',
    reset: 'Restablecer todos los ajustes',
  },
  sections: {
    vision: 'Visión y lectura',
    colorVision: 'Visión de color',
    themes: 'Temas de visualización',
    typography: 'Tipografía',
    reader: 'Lector de voz',
    motion: 'Movimiento',
  },
  vision: {
    hint: 'Aumenta el tamaño del texto y el espaciado para baja visión.',
    textSize: 'Tamaño del texto',
    spacing: 'Espaciado aumentado',
    spacingDesc: 'Más espacio entre letras, palabras y líneas.',
    readingMode: 'Modo lectura',
    readingModeDesc: 'Columna centrada, menos ruido visual.',
    enhancedFocus: 'Foco mejorado',
    enhancedFocusDesc: 'Contornos de enfoque de teclado más visibles.',
  },
  colorVision: {
    hint: 'Temas adaptativos para tu perfil de visión de color (UI, imágenes, vídeos y reproductores).',
    none: 'Visión estándar',
    deuteranopia: 'Deuteranopia',
    deuteranopiaDesc: 'Rojo-verde (conos verdes).',
    protanopia: 'Protanopia',
    protanopiaDesc: 'Rojo-verde (conos rojos).',
    tritanopia: 'Tritanopia',
    tritanopiaDesc: 'Azul-amarillo.',
    achromatopsia: 'Acromatopsia',
    achromatopsiaDesc: 'Monocromo / escala de grises.',
  },
  themes: en.themes,
  typography: {
    default: 'Fuente estándar',
    dyslexia: 'Apta para dislexia',
    dyslexiaDesc: 'Fuente Lexend y mayor espaciado (recomendado para dislexia).',
  },
  reader: {
    hint: 'Lector de voz integrado en el navegador. Selecciona texto para “Leer selección” o lee el contenido principal.',
    voice: 'Voz',
    voiceDefault: 'Predeterminada del sistema',
    speed: 'Velocidad de lectura',
    pitch: 'Tono de voz',
    readSelection: 'Leer selección',
    readPage: 'Leer contenido principal',
    play: 'Iniciar lectura',
    pause: 'Pausa',
    resume: 'Reanudar',
    stop: 'Detener',
    playing: 'Leyendo en voz alta...',
    paused: 'En pausa',
    started: 'Lectura por voz iniciada.',
    noSelection: 'No hay texto seleccionado. Resalta texto primero.',
    noContent: 'No se encontró contenido legible en esta página.',
    unsupported: 'La lectura por voz no es compatible con este navegador.',
    regionLabel: 'Controles del lector de voz',
    progress: 'Segmento {{current}} de {{total}}',
  },
  motion: {
    reduce: 'Reducir animaciones',
    reduceDesc: 'Minimiza movimientos y transiciones.',
  },
  announce: {
    textSize: 'Tamaño del texto ajustado a {{value}}%.',
    spacing: 'Espaciado actualizado.',
    readingMode: 'Modo lectura actualizado.',
    focus: 'Visibilidad del foco actualizada.',
    motion: 'Preferencia de movimiento actualizada.',
    reset: 'Ajustes de accesibilidad restablecidos.',
  },
} as const;

const pt = {
  panel: {
    open: 'Abrir definições de acessibilidade',
    close: 'Fechar definições de acessibilidade',
    title: 'Acessibilidade',
    subtitle: 'Personalize visualização, cores, tipografia e leitura por voz.',
    reset: 'Redefinir todas as definições',
  },
  sections: {
    vision: 'Visão e leitura',
    colorVision: 'Visão de cores',
    themes: 'Temas de visualização',
    typography: 'Tipografia',
    reader: 'Leitor de voz',
    motion: 'Movimento',
  },
  vision: {
    hint: 'Aumente o tamanho do texto e o espaçamento para baixa visão.',
    textSize: 'Tamanho do texto',
    spacing: 'Espaçamento aumentado',
    spacingDesc: 'Mais espaço entre letras, palavras e linhas.',
    readingMode: 'Modo leitura',
    readingModeDesc: 'Coluna centralizada, menos ruído visual.',
    enhancedFocus: 'Foco reforçado',
    enhancedFocusDesc: 'Contornos de foco do teclado mais visíveis.',
  },
  colorVision: {
    hint: 'Temas adaptativos para seu perfil de visão de cores (UI, imagens, vídeos e players).',
    none: 'Visão padrão',
    deuteranopia: 'Deuteranopia',
    deuteranopiaDesc: 'Vermelho-verde (cones verdes).',
    protanopia: 'Protanopia',
    protanopiaDesc: 'Vermelho-verde (cones vermelhos).',
    tritanopia: 'Tritanopia',
    tritanopiaDesc: 'Azul-amarelo.',
    achromatopsia: 'Acromatopsia',
    achromatopsiaDesc: 'Monocromático / escala de cinza.',
  },
  themes: en.themes,
  typography: {
    default: 'Fonte padrão',
    dyslexia: 'Amigável para dislexia',
    dyslexiaDesc: 'Fonte Lexend e maior espaçamento (recomendado para dislexia).',
  },
  reader: {
    hint: 'Leitor de voz integrado no navegador. Selecione um texto para “Ler seleção” ou leia o conteúdo principal.',
    voice: 'Voz',
    voiceDefault: 'Padrão do sistema',
    speed: 'Velocidade de leitura',
    pitch: 'Tom da voz',
    readSelection: 'Ler seleção',
    readPage: 'Ler conteúdo principal',
    play: 'Iniciar leitura',
    pause: 'Pausar',
    resume: 'Retomar',
    stop: 'Parar',
    playing: 'Lendo em voz alta...',
    paused: 'Pausado',
    started: 'Leitura por voz iniciada.',
    noSelection: 'Nenhum texto selecionado. Selecione um texto primeiro.',
    noContent: 'Nenhum conteúdo legível encontrado nesta página.',
    unsupported: 'Leitura por voz não é suportada neste navegador.',
    regionLabel: 'Controles do leitor de voz',
    progress: 'Segmento {{current}} de {{total}}',
  },
  motion: {
    reduce: 'Reduzir animações',
    reduceDesc: 'Minimiza movimento e transições.',
  },
  announce: {
    textSize: 'Tamanho do texto definido para {{value}}%.',
    spacing: 'Espaçamento atualizado.',
    readingMode: 'Modo leitura atualizado.',
    focus: 'Visibilidade do foco atualizada.',
    motion: 'Preferência de movimento atualizada.',
    reset: 'Definições de acessibilidade redefinidas.',
  },
} as const;

const ru = {
  panel: {
    open: 'Открыть настройки доступности',
    close: 'Закрыть настройки доступности',
    title: 'Доступность',
    subtitle: 'Настройте отображение, цвета, типографику и голосовое чтение.',
    reset: 'Сбросить все настройки',
  },
  sections: {
    vision: 'Зрение и чтение',
    colorVision: 'Цветовое восприятие',
    themes: 'Темы отображения',
    typography: 'Типографика',
    reader: 'Голосовой диктор',
    motion: 'Анимация',
  },
  vision: {
    hint: 'Увеличьте размер текста и интервалы при слабом зрении.',
    textSize: 'Размер текста',
    spacing: 'Увеличенные интервалы',
    spacingDesc: 'Больше пространства между буквами, словами и строками.',
    readingMode: 'Режим чтения',
    readingModeDesc: 'Центральная колонка, меньше визуального шума.',
    enhancedFocus: 'Усиленный фокус',
    enhancedFocusDesc: 'Более заметные контуры клавиатурного фокуса.',
  },
  colorVision: {
    hint: 'Адаптивные темы для вашего профиля цветового восприятия (интерфейс, изображения, видео и плееры).',
    none: 'Стандартное зрение',
    deuteranopia: 'Дейтеранопия',
    deuteranopiaDesc: 'Красно-зелёный (зелёные колбочки).',
    protanopia: 'Протанопия',
    protanopiaDesc: 'Красно-зелёный (красные колбочки).',
    tritanopia: 'Тританопия',
    tritanopiaDesc: 'Сине-жёлтый.',
    achromatopsia: 'Ахроматопсия',
    achromatopsiaDesc: 'Монохром / оттенки серого.',
  },
  themes: en.themes,
  typography: {
    default: 'Стандартный шрифт',
    dyslexia: 'Для дислексии',
    dyslexiaDesc: 'Шрифт Lexend и увеличенные интервалы (рекомендуется при дислексии).',
  },
  reader: {
    hint: 'Встроенный в браузер голосовой диктор. Выделите текст для “Читать выделение” или озвучьте основной контент.',
    voice: 'Голос',
    voiceDefault: 'Системный по умолчанию',
    speed: 'Скорость чтения',
    pitch: 'Высота голоса',
    readSelection: 'Читать выделение',
    readPage: 'Читать основной контент',
    play: 'Начать чтение',
    pause: 'Пауза',
    resume: 'Продолжить',
    stop: 'Остановить',
    playing: 'Озвучивание...',
    paused: 'На паузе',
    started: 'Голосовое чтение запущено.',
    noSelection: 'Текст не выделен. Сначала выделите текст на странице.',
    noContent: 'На странице не найден читаемый контент.',
    unsupported: 'Голосовое чтение не поддерживается в этом браузере.',
    regionLabel: 'Управление голосовым диктором',
    progress: 'Сегмент {{current}} из {{total}}',
  },
  motion: {
    reduce: 'Уменьшить анимации',
    reduceDesc: 'Минимизирует движения и переходы.',
  },
  announce: {
    textSize: 'Размер текста установлен на {{value}}%.',
    spacing: 'Интервалы обновлены.',
    readingMode: 'Режим чтения обновлён.',
    focus: 'Видимость фокуса обновлена.',
    motion: 'Параметр движения обновлён.',
    reset: 'Настройки доступности сброшены.',
  },
} as const;

const sw = {
  panel: {
    open: 'Fungua mipangilio ya ufikivu',
    close: 'Funga mipangilio ya ufikivu',
    title: 'Ufikivu',
    subtitle: 'Binafsisha mwonekano, rangi, uchapaji na usomaji wa sauti.',
    reset: 'Weka upya mipangilio yote',
  },
  sections: {
    vision: 'Uoni na usomaji',
    colorVision: 'Uoni wa rangi',
    themes: 'Mandhari ya mwonekano',
    typography: 'Uchapaji',
    reader: 'Msomaji wa sauti',
    motion: 'Mwendo',
  },
  vision: {
    hint: 'Ongeza ukubwa wa maandishi na nafasi kwa uoni hafifu.',
    textSize: 'Ukubwa wa maandishi',
    spacing: 'Nafasi imeongezwa',
    spacingDesc: 'Nafasi zaidi kati ya herufi, maneno na mistari.',
    readingMode: 'Hali ya kusoma',
    readingModeDesc: 'Safu iliyokatikati, kelele ndogo ya kuona.',
    enhancedFocus: 'Fokasi iliyoimarishwa',
    enhancedFocusDesc: 'Mipaka ya fokasi ya kibodi inaonekana zaidi.',
  },
  colorVision: {
    hint: 'Mandhari yanayobadilika kulingana na wasifu wako wa uoni wa rangi (UI, picha, video na vichezaji).',
    none: 'Uoni wa kawaida',
    deuteranopia: 'Deuteranopia',
    deuteranopiaDesc: 'Nyekundu-kijani (koni za kijani).',
    protanopia: 'Protanopia',
    protanopiaDesc: 'Nyekundu-kijani (koni za nyekundu).',
    tritanopia: 'Tritanopia',
    tritanopiaDesc: 'Bluu-njano.',
    achromatopsia: 'Achromatopsia',
    achromatopsiaDesc: 'Monokromu / kijivu.',
  },
  themes: en.themes,
  typography: {
    default: 'Fonti ya kawaida',
    dyslexia: 'Rafiki kwa disleksia',
    dyslexiaDesc: 'Fonti ya Lexend na nafasi kubwa zaidi (inapendekezwa kwa disleksia).',
  },
  reader: {
    hint: 'Msomaji wa sauti uliojengwa ndani ya kivinjari. Chagua maandishi kwa “Soma kilichochaguliwa” au soma maudhui makuu.',
    voice: 'Sauti',
    voiceDefault: 'Chaguo-msingi wa mfumo',
    speed: 'Kasi ya usomaji',
    pitch: 'Urefu wa sauti',
    readSelection: 'Soma kilichochaguliwa',
    readPage: 'Soma maudhui makuu',
    play: 'Anza kusoma',
    pause: 'Sitisha',
    resume: 'Endelea',
    stop: 'Simamisha',
    playing: 'Inasoma kwa sauti...',
    paused: 'Imesitishwa',
    started: 'Usomaji wa sauti umeanza.',
    noSelection: 'Hakuna maandishi yaliyochaguliwa. Chagua maandishi kwanza.',
    noContent: 'Hakuna maudhui yanayosomeka yaliyopatikana kwenye ukurasa huu.',
    unsupported: 'Usomaji wa sauti hauungwa mkono na kivinjari hiki.',
    regionLabel: 'Vidhibiti vya msomaji wa sauti',
    progress: 'Sehemu {{current}} ya {{total}}',
  },
  motion: {
    reduce: 'Punguza michoro',
    reduceDesc: 'Punguza mwendo na mabadiliko.',
  },
  announce: {
    textSize: 'Ukubwa wa maandishi umewekwa kuwa {{value}}%.',
    spacing: 'Nafasi imesasishwa.',
    readingMode: 'Hali ya kusoma imesasishwa.',
    focus: 'Mwonekano wa fokasi umesasishwa.',
    motion: 'Mapendeleo ya mwendo yamesasishwa.',
    reset: 'Mipangilio ya ufikivu imewekwa upya.',
  },
} as const;

const zh = {
  panel: {
    open: '打开无障碍设置',
    close: '关闭无障碍设置',
    title: '无障碍',
    subtitle: '自定义显示、颜色、字体与语音朗读。',
    reset: '重置全部设置',
  },
  sections: {
    vision: '视觉与阅读',
    colorVision: '色觉',
    themes: '显示主题',
    typography: '字体排版',
    reader: '语音朗读器',
    motion: '动效',
  },
  vision: {
    hint: '为低视力用户增大文字和间距。',
    textSize: '文字大小',
    spacing: '增大间距',
    spacingDesc: '增加字母、单词和行之间的间隔。',
    readingMode: '阅读模式',
    readingModeDesc: '居中栏布局，减少视觉干扰。',
    enhancedFocus: '增强焦点',
    enhancedFocusDesc: '键盘焦点边框更明显。',
  },
  colorVision: {
    hint: '根据色觉类型自适应主题（界面、图片、视频和嵌入播放器）。',
    none: '标准视觉',
    deuteranopia: '绿色盲',
    deuteranopiaDesc: '红绿（绿色视锥）。',
    protanopia: '红色盲',
    protanopiaDesc: '红绿（红色视锥）。',
    tritanopia: '蓝黄色盲',
    tritanopiaDesc: '蓝黄。',
    achromatopsia: '全色盲',
    achromatopsiaDesc: '单色 / 灰度。',
  },
  themes: en.themes,
  typography: {
    default: '标准字体',
    dyslexia: '阅读障碍友好',
    dyslexiaDesc: 'Lexend 字体并增加间距（推荐用于阅读障碍）。',
  },
  reader: {
    hint: '浏览器内置语音朗读。先选中文本使用“朗读所选”，或直接朗读主要内容。',
    voice: '语音',
    voiceDefault: '系统默认',
    speed: '朗读速度',
    pitch: '音调',
    readSelection: '朗读所选',
    readPage: '朗读主要内容',
    play: '开始朗读',
    pause: '暂停',
    resume: '继续',
    stop: '停止',
    playing: '正在朗读...',
    paused: '已暂停',
    started: '已开始语音朗读。',
    noSelection: '未选择文本。请先选择页面文本。',
    noContent: '此页面未找到可朗读内容。',
    unsupported: '此浏览器不支持语音朗读。',
    regionLabel: '语音朗读控制',
    progress: '第 {{current}} 段 / 共 {{total}} 段',
  },
  motion: {
    reduce: '减少动画',
    reduceDesc: '尽量减少动效和过渡。',
  },
  announce: {
    textSize: '文字大小已设置为 {{value}}%。',
    spacing: '间距已更新。',
    readingMode: '阅读模式已更新。',
    focus: '焦点可见性已更新。',
    motion: '动效偏好已更新。',
    reset: '无障碍设置已重置。',
  },
} as const;

const hi = {
  panel: {
    open: 'एक्सेसिबिलिटी सेटिंग्स खोलें',
    close: 'एक्सेसिबिलिटी सेटिंग्स बंद करें',
    title: 'एक्सेसिबिलिटी',
    subtitle: 'डिस्प्ले, रंग, टाइपोग्राफी और वॉइस रीडिंग को अनुकूलित करें।',
    reset: 'सभी सेटिंग्स रीसेट करें',
  },
  sections: {
    vision: 'दृष्टि और पढ़ना',
    colorVision: 'रंग दृष्टि',
    themes: 'डिस्प्ले थीम',
    typography: 'टाइपोग्राफी',
    reader: 'वॉइस रीडर',
    motion: 'मोशन',
  },
  vision: {
    hint: 'कम दृष्टि के लिए टेक्स्ट आकार और स्पेसिंग बढ़ाएँ।',
    textSize: 'टेक्स्ट आकार',
    spacing: 'बढ़ी हुई स्पेसिंग',
    spacingDesc: 'अक्षरों, शब्दों और पंक्तियों के बीच अधिक स्थान।',
    readingMode: 'रीडिंग मोड',
    readingModeDesc: 'केंद्रित कॉलम, कम दृश्य शोर।',
    enhancedFocus: 'बेहतर फोकस',
    enhancedFocusDesc: 'कीबोर्ड फोकस आउटलाइन अधिक स्पष्ट।',
  },
  colorVision: {
    hint: 'आपकी रंग दृष्टि प्रोफ़ाइल के लिए अनुकूली थीम (UI, चित्र, वीडियो और एम्बेडेड प्लेयर)।',
    none: 'सामान्य दृष्टि',
    deuteranopia: 'ड्यूटेरानोपिया',
    deuteranopiaDesc: 'लाल-हरा (हरे कोन)।',
    protanopia: 'प्रोटानोपिया',
    protanopiaDesc: 'लाल-हरा (लाल कोन)।',
    tritanopia: 'ट्राइटानोपिया',
    tritanopiaDesc: 'नीला-पीला।',
    achromatopsia: 'अक्रोमैटोप्सिया',
    achromatopsiaDesc: 'मोनोक्रोम / ग्रेस्केल।',
  },
  themes: en.themes,
  typography: {
    default: 'मानक फ़ॉन्ट',
    dyslexia: 'डिस्लेक्सिया-फ्रेंडली',
    dyslexiaDesc: 'Lexend फ़ॉन्ट और बढ़ी स्पेसिंग (डिस्लेक्सिया के लिए अनुशंसित)।',
  },
  reader: {
    hint: 'ब्राउज़र में बिल्ट-इन वॉइस रीडर। “चयन पढ़ें” के लिए टेक्स्ट चुनें या मुख्य सामग्री पढ़ें।',
    voice: 'आवाज़',
    voiceDefault: 'सिस्टम डिफ़ॉल्ट',
    speed: 'पढ़ने की गति',
    pitch: 'आवाज़ की पिच',
    readSelection: 'चयन पढ़ें',
    readPage: 'मुख्य सामग्री पढ़ें',
    play: 'पढ़ना शुरू करें',
    pause: 'रोकें',
    resume: 'जारी रखें',
    stop: 'बंद करें',
    playing: 'आवाज़ में पढ़ा जा रहा है...',
    paused: 'रुका हुआ',
    started: 'वॉइस रीडिंग शुरू हुई।',
    noSelection: 'कोई टेक्स्ट चयनित नहीं है। पहले टेक्स्ट चुनें।',
    noContent: 'इस पेज पर पढ़ने योग्य सामग्री नहीं मिली।',
    unsupported: 'इस ब्राउज़र में टेक्स्ट-टू-स्पीच समर्थित नहीं है।',
    regionLabel: 'वॉइस रीडर नियंत्रण',
    progress: 'सेगमेंट {{current}} / {{total}}',
  },
  motion: {
    reduce: 'एनीमेशन कम करें',
    reduceDesc: 'मोशन और ट्रांज़िशन कम करें।',
  },
  announce: {
    textSize: 'टेक्स्ट आकार {{value}}% पर सेट किया गया।',
    spacing: 'स्पेसिंग अपडेट हुई।',
    readingMode: 'रीडिंग मोड अपडेट हुआ।',
    focus: 'फोकस दृश्यता अपडेट हुई।',
    motion: 'मोशन प्राथमिकता अपडेट हुई।',
    reset: 'एक्सेसिबिलिटी सेटिंग्स रीसेट हो गईं।',
  },
} as const;

const id = {
  panel: {
    open: 'Buka pengaturan aksesibilitas',
    close: 'Tutup pengaturan aksesibilitas',
    title: 'Aksesibilitas',
    subtitle: 'Sesuaikan tampilan, warna, tipografi, dan pembacaan suara.',
    reset: 'Setel ulang semua pengaturan',
  },
  sections: {
    vision: 'Penglihatan & membaca',
    colorVision: 'Penglihatan warna',
    themes: 'Tema tampilan',
    typography: 'Tipografi',
    reader: 'Pembaca suara',
    motion: 'Gerakan',
  },
  vision: {
    hint: 'Perbesar ukuran teks dan jarak untuk penglihatan rendah.',
    textSize: 'Ukuran teks',
    spacing: 'Jarak diperbesar',
    spacingDesc: 'Lebih banyak jarak antar huruf, kata, dan baris.',
    readingMode: 'Mode membaca',
    readingModeDesc: 'Kolom terpusat, gangguan visual berkurang.',
    enhancedFocus: 'Fokus ditingkatkan',
    enhancedFocusDesc: 'Garis fokus keyboard lebih terlihat.',
  },
  colorVision: {
    hint: 'Tema adaptif untuk profil penglihatan warna Anda (UI, gambar, video, dan pemutar tertanam).',
    none: 'Penglihatan standar',
    deuteranopia: 'Deuteranopia',
    deuteranopiaDesc: 'Merah-hijau (kerucut hijau).',
    protanopia: 'Protanopia',
    protanopiaDesc: 'Merah-hijau (kerucut merah).',
    tritanopia: 'Tritanopia',
    tritanopiaDesc: 'Biru-kuning.',
    achromatopsia: 'Achromatopsia',
    achromatopsiaDesc: 'Monokrom / skala abu-abu.',
  },
  themes: en.themes,
  typography: {
    default: 'Font standar',
    dyslexia: 'Ramah disleksia',
    dyslexiaDesc: 'Font Lexend dan spasi lebih besar (disarankan untuk disleksia).',
  },
  reader: {
    hint: 'Pembaca suara bawaan browser. Pilih teks untuk “Baca pilihan” atau baca konten utama.',
    voice: 'Suara',
    voiceDefault: 'Default sistem',
    speed: 'Kecepatan baca',
    pitch: 'Pitch suara',
    readSelection: 'Baca pilihan',
    readPage: 'Baca konten utama',
    play: 'Mulai membaca',
    pause: 'Jeda',
    resume: 'Lanjutkan',
    stop: 'Berhenti',
    playing: 'Sedang dibacakan...',
    paused: 'Dijeda',
    started: 'Pembacaan suara dimulai.',
    noSelection: 'Tidak ada teks terpilih. Sorot teks terlebih dahulu.',
    noContent: 'Tidak ada konten yang dapat dibaca di halaman ini.',
    unsupported: 'Text-to-speech tidak didukung di browser ini.',
    regionLabel: 'Kontrol pembaca suara',
    progress: 'Segmen {{current}} dari {{total}}',
  },
  motion: {
    reduce: 'Kurangi animasi',
    reduceDesc: 'Minimalkan gerakan dan transisi.',
  },
  announce: {
    textSize: 'Ukuran teks diatur ke {{value}}%.',
    spacing: 'Jarak diperbarui.',
    readingMode: 'Mode membaca diperbarui.',
    focus: 'Visibilitas fokus diperbarui.',
    motion: 'Preferensi gerakan diperbarui.',
    reset: 'Pengaturan aksesibilitas direset.',
  },
} as const;

const ar = {
  panel: {
    open: 'فتح إعدادات إمكانية الوصول',
    close: 'إغلاق إعدادات إمكانية الوصول',
    title: 'إمكانية الوصول',
    subtitle: 'خصّص العرض والألوان والطباعة والقراءة الصوتية.',
    reset: 'إعادة تعيين جميع الإعدادات',
  },
  sections: {
    vision: 'الرؤية والقراءة',
    colorVision: 'رؤية الألوان',
    themes: 'سمات العرض',
    typography: 'الطباعة',
    reader: 'قارئ صوتي',
    motion: 'الحركة',
  },
  vision: {
    hint: 'زد حجم النص والمسافات لضعف البصر.',
    textSize: 'حجم النص',
    spacing: 'تباعد مُحسّن',
    spacingDesc: 'مساحة أكبر بين الحروف والكلمات والأسطر.',
    readingMode: 'وضع القراءة',
    readingModeDesc: 'عمود مركزي وضوضاء بصرية أقل.',
    enhancedFocus: 'تركيز مُحسّن',
    enhancedFocusDesc: 'إطارات تركيز لوحة المفاتيح أكثر وضوحًا.',
  },
  colorVision: {
    hint: 'سمات تكيفية حسب نمط رؤية الألوان لديك (الواجهة، الصور، الفيديو والمشغلات المضمنة).',
    none: 'رؤية قياسية',
    deuteranopia: 'عمى أخضر',
    deuteranopiaDesc: 'أحمر-أخضر (مخاريط خضراء).',
    protanopia: 'عمى أحمر',
    protanopiaDesc: 'أحمر-أخضر (مخاريط حمراء).',
    tritanopia: 'عمى أزرق-أصفر',
    tritanopiaDesc: 'أزرق-أصفر.',
    achromatopsia: 'انعدام تمييز الألوان',
    achromatopsiaDesc: 'أحادي اللون / تدرج رمادي.',
  },
  themes: en.themes,
  typography: {
    default: 'خط قياسي',
    dyslexia: 'ملائم لعُسر القراءة',
    dyslexiaDesc: 'خط Lexend وتباعد أكبر (موصى به لعسر القراءة).',
  },
  reader: {
    hint: 'قارئ صوتي مدمج في المتصفح. حدّد نصًا لاستخدام “قراءة التحديد” أو اقرأ المحتوى الرئيسي.',
    voice: 'الصوت',
    voiceDefault: 'افتراضي النظام',
    speed: 'سرعة القراءة',
    pitch: 'حدة الصوت',
    readSelection: 'قراءة التحديد',
    readPage: 'قراءة المحتوى الرئيسي',
    play: 'بدء القراءة',
    pause: 'إيقاف مؤقت',
    resume: 'استئناف',
    stop: 'إيقاف',
    playing: 'تتم القراءة بصوت عالٍ...',
    paused: 'متوقف مؤقتًا',
    started: 'تم بدء القراءة الصوتية.',
    noSelection: 'لا يوجد نص محدد. حدّد نصًا أولاً.',
    noContent: 'لم يتم العثور على محتوى قابل للقراءة في هذه الصفحة.',
    unsupported: 'القراءة الصوتية غير مدعومة في هذا المتصفح.',
    regionLabel: 'عناصر التحكم بالقارئ الصوتي',
    progress: 'المقطع {{current}} من {{total}}',
  },
  motion: {
    reduce: 'تقليل الرسوم المتحركة',
    reduceDesc: 'تقليل الحركة والانتقالات.',
  },
  announce: {
    textSize: 'تم تعيين حجم النص إلى {{value}}%.',
    spacing: 'تم تحديث التباعد.',
    readingMode: 'تم تحديث وضع القراءة.',
    focus: 'تم تحديث وضوح التركيز.',
    motion: 'تم تحديث تفضيل الحركة.',
    reset: 'تمت إعادة تعيين إعدادات إمكانية الوصول.',
  },
} as const;

export function getA11yFallback(languageCode: string) {
  const code = languageCode.split('-')[0].toLowerCase();
  const map = { en, fr, es, pt, ru, sw, zh, hi, id, ar } as const;
  return map[code as keyof typeof map] ?? en;
}
